import {
  Button,
  Grid,
  Group,
  InputLabel,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
} from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useHotkeys } from '@mantine/hooks'
import type { Asset, AssetAssignment, AssetAssignmentSchema, AssetTarget, User } from '@opsopti/shared'
import {
  castAssetAssignmentToForm,
  getHasOneRelationship,
  getHasOneRelationshipId,
  isFieldsListValid,
  useAssetAssignmentCreateMutation,
  useAssetAssignmentForm,
  useAssetAssignmentQuery,
  useAssetAssignmentUpdateMutation,
  useAssetQuery,
  useAssetsQuery,
  useAssetTargetsQuery,
  useGlobalState,
  useUsersQuery,
} from '@opsopti/shared'
import type {
  CreateAssetAssignmentResponse,
  UpdateAssetAssignmentResponse,
} from '@opsopti/shared/src/services/api/api.types'
import { useAreFormQueriesFetched } from '@opsopti/shared/src/utils/tanstackHelpers'
import { IconArrowsDown } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { createContext, Fragment, useContext, useEffect, useMemo, useState } from 'react'
import type { z } from 'zod'

import type { ScreenProps } from '@/components/Screen'
import { Screen } from '@/components/Screen'
import { SectionedForm, type SectionedFormProps } from '@/components/SectionedForm'
import { getFormScreenTitle, showErrorNotification, showSuccessNotification } from '@/utils/componentCommons'
import { goBack } from '@/utils/navigation'

import { TableScreenDescription, tableScreenIcon } from './TableScreen'

type AssetSentryAssignmentFormScreenProps = {
  type?: 'screen' | 'drawer'
  params?: undefined | { id: string }
  additionalParams?: undefined | { prevAssignmentId?: string | null; assetId?: string | null }
  goBackFn?: () => void
  onAfterSubmitFn?: (group: CreateAssetAssignmentResponse | UpdateAssetAssignmentResponse) => void
}

type FormSection = {
  id: string | null
  type: 'screen' | 'drawer'
  form: UseFormReturnType<z.infer<AssetAssignmentSchema>>
  prevAssignment?: AssetAssignment
  assignment?: AssetAssignment
  asset?: Asset
  users: User[]
  assets: Asset[]
  targets: AssetTarget[]
  isMutating: boolean
  submitFn: () => Promise<void>
  goBackFn: () => void
}

const AssetAssignmentFormDataContext = createContext<FormSection>({} as FormSection)
const useAssetAssignmentFormData = () => useContext(AssetAssignmentFormDataContext)

//
// Notes: Currently, this screen is built for the "new" flow.
//
export const AssetSentryAssignmentFormScreen = observer(function AssetSentryAssignmentFormScreen(
  props: AssetSentryAssignmentFormScreenProps,
) {
  const { uiStore, authenticationStore } = useGlobalState()

  const id = props.params?.id ?? null
  const isNew = useMemo(() => !id, [id])
  const type = props.type ?? 'screen'
  const { prevAssignmentId = null, assetId = null } = props.additionalParams ?? {}

  const assignmentQuery = useAssetAssignmentQuery({ params: { id } })
  const prevAssignmentQuery = useAssetAssignmentQuery({ params: { id: prevAssignmentId } })
  const assetQuery = useAssetQuery({ params: { id: assetId } })

  const { form } = useAssetAssignmentForm({ creator: authenticationStore.user?.id })

  const assetsQuery = useAssetsQuery({
    params: {
      depth: '1',
      where: { visibilityStatus: { equals: 'active' } },
      select: {
        name: true,
        identifier: true,
        assetGroup: true,
        nameWithIdentifier: true,
      },
    },
  })
  const usersQuery = useUsersQuery({
    params: {
      depth: '0',
      where: { visibilityStatus: { not_equals: 'archived' } },
      select: {
        firstName: true,
        lastName: true,
        fullName: true,
      },
    },
  })
  const targetsQuery = useAssetTargetsQuery({
    params: {
      depth: '0',
      where: { visibilityStatus: { not_equals: 'archived' } },
      select: {
        type: true,
        name: true,
      },
    },
  })
  const assignmentCreateMutation = useAssetAssignmentCreateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })
  const assignmentUpdateMutation = useAssetAssignmentUpdateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  const areQueriesLoaded = useAreFormQueriesFetched([
    !isNew ? assignmentQuery.isFetched : null,
    !!prevAssignmentId ? prevAssignmentQuery.isFetched : null,
    !!assetId ? assetQuery.isFetched : null,
    assetsQuery.isFetched,
    usersQuery.isFetched,
    targetsQuery.isFetched,
  ])

  useEffect(() => {
    if (areQueriesLoaded) {
      form.initialize(castAssetAssignmentToForm(assignmentQuery.data))
      if (assetId) {
        form.setFieldValue('creator', authenticationStore.user?.id!)
        form.setFieldValue('asset', assetQuery.data!.id)
        const assetGroup = getHasOneRelationship(assetQuery.data?.assetGroup)
        const defaultTargetId = getHasOneRelationshipId(assetGroup?.defaultTarget)
        form.setFieldValue('assetTarget', defaultTargetId)
      }
      uiStore.hideLoadingOverlay()
    } else {
      uiStore.showLoadingOverlay()
    }
  }, [assetId, prevAssignmentId, areQueriesLoaded])

  useEffect(() => uiStore.hideLoadingOverlay, [])

  useHotkeys([['mod+S', submitFn]], [])

  const goBackFn = props.goBackFn ?? goBack

  async function submitFn() {
    const validations = form.validate()

    if (validations.hasErrors) {
      showErrorNotification({ title: 'Form Errors', message: 'Please fill out all required fields before submitting.' })
      return
    }

    let response: CreateAssetAssignmentResponse | UpdateAssetAssignmentResponse | undefined

    const values = form.getValues()
    const assignees = [authenticationStore.user?.id!, ...values.assignees]

    if (isNew) {
      response = await assignmentCreateMutation.mutateAsync({ ...values, assignees })
    } else {
      response = await assignmentUpdateMutation.mutateAsync({ ...values, id: id!, assignees })
    }

    props.onAfterSubmitFn?.(response)

    goBackFn()
  }

  const sections = useMemo(
    () =>
      [
        {
          id: 'details',
          title: 'Details',
          subtitle: 'Basic details about the assignment.',
          collapsible: false,
          valid:
            isFieldsListValid(form, ['creator', 'asset']) &&
            (isFieldsListValid(form, ['assetTarget']) || isFieldsListValid(form, ['assetTargetAsset'])),
          ContentComponent: GeneralFields,
        },
        {
          id: 'summary',
          title: 'Summary',
          subtitle: 'Information about how this asset will be moved.',
          collapsible: false,
          hidden: !prevAssignmentId,
          ContentComponent: Summary,
        },
        {
          id: 'actions',
          collapsible: false,
          ContentComponent: FormActions,
        },
      ] satisfies SectionedFormProps['sections'],
    [form],
  )

  const isMutating = assignmentCreateMutation.isPending || assignmentUpdateMutation.isPending
  const FormWrapper = type === 'screen' ? Screen : Fragment
  const FormWrapperProps: ScreenProps =
    type === 'screen'
      ? {
          maw: 1440,
          title: getFormScreenTitle(id, 'Assignment'),
          icon: tableScreenIcon['assignments'],
          description: <TableScreenDescription entity="assignments" />,
        }
      : ({} as ScreenProps)

  return (
    <FormWrapper {...FormWrapperProps}>
      <AssetAssignmentFormDataContext.Provider
        value={{
          id,
          type,
          form,
          submitFn,
          goBackFn,
          isMutating,
          assignment: assignmentQuery.data,
          prevAssignment: prevAssignmentQuery.data,
          asset: assetQuery.data,
          targets: targetsQuery.data?.docs ?? [],
          users: usersQuery.data?.docs ?? [],
          assets: assetsQuery.data?.docs ?? [],
        }}>
        <SectionedForm sections={sections} sectionsWithBorder={type === 'screen'} />
      </AssetAssignmentFormDataContext.Provider>
    </FormWrapper>
  )
})

enum TargetControl {
  Target = 'target',
  Asset = 'asset',
}

function GeneralFields() {
  const [targetControl, setTargetControl] = useState<TargetControl>(TargetControl.Target)
  const { authenticationStore } = useGlobalState()
  const props = useAssetAssignmentFormData()

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Textarea
          withAsterisk
          disabled
          autosize
          minRows={1}
          label="Asset"
          description="The asset being assigned."
          value={props.asset?.nameWithIdentifier ?? ''}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          withAsterisk
          disabled
          autosize
          minRows={1}
          label="Assigned By"
          description="The user who is assigning this asset."
          value={authenticationStore.user?.fullName ?? ''}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <MultiSelect
          searchable
          clearable
          label="Assignees"
          placeholder="Select assignees..."
          description="Additional users who are responsible for this assignment."
          data={props.users
            .filter(u => u.id !== authenticationStore.user?.id)
            .map(u => ({ value: u.id, label: u.fullName ?? '' }))}
          {...props.form.getInputProps('assignees')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Stack gap={12}>
          <InputLabel required>{`Where Is This Being Assigned To?`}</InputLabel>
          <SegmentedControl
            value={targetControl}
            onChange={e => {
              setTargetControl(e as TargetControl)
              props.form.setFieldValue('assetTarget', null)
              props.form.setFieldValue('assetTargetAsset', null)
              props.form.clearFieldError('assetTarget')
              props.form.clearFieldError('assetTargetAsset')
            }}
            fullWidth
            size="md"
            radius="xs"
            data={[
              {
                label: 'Target',
                value: TargetControl.Target,
              },
              {
                label: 'Asset',
                value: TargetControl.Asset,
              },
            ]}
          />
          {targetControl === TargetControl.Target ? (
            <Select
              searchable
              clearable
              placeholder="Select a target..."
              description="The physical location or team where this item is being assigned to."
              data={props.targets.reduce(
                (acc: Array<{ group: string; items: Array<{ label: string; value: string }> }>, t) => {
                  const gIndex = acc.findIndex(g => g.group === t.type?.toUpperCase())

                  if (gIndex === -1) {
                    acc.push({ group: t.type?.toUpperCase() ?? '', items: [{ label: t.name, value: t.id }] })
                  } else {
                    acc[gIndex].items.push({ label: t.name, value: t.id })
                  }

                  return acc
                },
                [],
              )}
              {...props.form.getInputProps('assetTarget')}
              onChange={e => {
                props.form.getInputProps('assetTarget').onChange(e)
                props.form.setFieldValue('assetTargetAsset', null)
                props.form.clearFieldError('assetTargetAsset')
              }}
              onClear={() => {
                props.form.setFieldValue('assetTarget', null)
                props.form.setFieldValue('assetTargetAsset', null)
                props.form.clearFieldError('assetTargetAsset')
              }}
            />
          ) : (
            <Select
              searchable
              clearable
              placeholder="Select an asset..."
              description="Another asset that will store this item."
              data={props.assets.map(t => {
                return {
                  value: t.id,
                  label: t?.nameWithIdentifier ?? '',
                }
              })}
              {...props.form.getInputProps('assetTargetAsset')}
              onChange={e => {
                props.form.getInputProps('assetTargetAsset').onChange(e)
                props.form.setFieldValue('assetTarget', null)
                props.form.clearFieldError('assetTarget')
              }}
              onClear={() => {
                props.form.setFieldValue('assetTargetAsset', null)
                props.form.setFieldValue('assetTarget', null)
                props.form.clearFieldError('assetTarget')
              }}
            />
          )}
        </Stack>
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Supervisors"
          autosize
          minRows={4}
          description="Notes that can be used for internal purposes. Only visible to Supervisors."
          {...props.form.getInputProps('internalNotes')}
        />
      </Grid.Col>
    </Grid>
  )
}

function Summary() {
  const props = useAssetAssignmentFormData()

  const prevTarget = getHasOneRelationship(props.prevAssignment?.assetTarget)
  const prevTargetAsset = getHasOneRelationship(props.prevAssignment?.assetTargetAsset)
  const PrevIcon = prevTarget ? tableScreenIcon['targets'] : tableScreenIcon['assets']
  const prevTargetString = prevTarget ? prevTarget.name : prevTargetAsset?.nameWithIdentifier

  const nextTarget = props.targets.find(t => t.id === props.form.getValues().assetTarget)
  const nextTargetAsset = props.assets.find(t => t.id === props.form.getValues().assetTargetAsset)
  const NextIcon = nextTarget ? tableScreenIcon['targets'] : tableScreenIcon['assets']
  const nextTargetString = nextTarget ? nextTarget.name : nextTargetAsset?.nameWithIdentifier

  return (
    <Stack>
      <Group align="center">
        <ThemeIcon size={28}>
          <PrevIcon />
        </ThemeIcon>

        <Text ta="center" fw={500}>
          {prevTargetString}
        </Text>
      </Group>

      <ThemeIcon size={28} variant="transparent">
        <IconArrowsDown />
      </ThemeIcon>

      <Group align="center">
        <ThemeIcon size={28}>
          <NextIcon />
        </ThemeIcon>
        <Text ta="center" fw={500} c={!!nextTargetString ? undefined : 'dimmed'}>
          {nextTargetString || 'Not Selected'}
        </Text>
      </Group>
    </Stack>
  )
}

function FormActions() {
  const props = useAssetAssignmentFormData()

  return (
    <Stack gap="xxl">
      <Group gap="xl">
        <Button size="lg" radius="sm" onClick={props.submitFn} loading={props.isMutating}>
          {props.id ? 'Update Assignment Details' : 'Assign'}
        </Button>

        {!props.isMutating && (
          <Button size="sm" radius="sm" variant="light" color="gray" onClick={props.goBackFn}>
            Cancel
          </Button>
        )}
      </Group>
    </Stack>
  )
}
